import { X } from 'lucide-react';
import { Dialog as RadixDialog } from 'radix-ui';
import {
  forwardRef,
  useCallback,
  useState,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type ReactNode,
} from 'react';

import { cn } from '@/utils/cn';

import { DialogPortalContext } from './popover-portal';
import { ScrollArea } from './scroll-area';

/**
 * The dialog shell.
 *
 * Two structural decisions, both from
 * `documentation/architecture/dialog-dropdown-portal-architecture.md`:
 *
 * 1. **`DialogContent` never clips.** It is `overflow-visible`, always. A long body
 *    scrolls through `DialogBody` / an inner `ScrollArea` instead. An `overflow-auto`
 *    shell cuts every dropdown opened inside it in half.
 * 2. **It publishes its own element.** Pickers with `popoverPortal="dialog"` portal
 *    into it, which puts their menus inside the dialog's stacking context — above
 *    the overlay rather than behind it.
 */

export const Dialog = RadixDialog.Root;
export const DialogTrigger = RadixDialog.Trigger;
export const DialogClose = RadixDialog.Close;

export interface DialogContentProps
  extends ComponentPropsWithoutRef<typeof RadixDialog.Content> {
  /** Hides the corner close control for a dialog that must be answered. */
  hideCloseButton?: boolean;
  /** Accessible name for the close control; the dialog owns no strings of its own. */
  closeLabel?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
}

const SIZES: Record<NonNullable<DialogContentProps['size']>, string> = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-lg',
  lg: 'sm:max-w-2xl',
  xl: 'sm:max-w-4xl',
  '2xl': 'sm:max-w-6xl',
  full: 'sm:max-w-[calc(100vw-4rem)]',
};

export const DialogContent = forwardRef<
  ElementRef<typeof RadixDialog.Content>,
  DialogContentProps
>(
  (
    { className, children, hideCloseButton, closeLabel, size = 'md', ...props },
    forwardedRef,
  ) => {
    const [element, setElement] = useState<HTMLDivElement | null>(null);

    // A callback ref rather than a `useRef`, because the portal context must
    // re-render its consumers once the element exists. A ref object mutates
    // silently and the first picker to open would find nothing there.
    const setRefs = useCallback(
      (node: HTMLDivElement | null) => {
        setElement(node);
        if (typeof forwardedRef === 'function') forwardedRef(node);
        else if (forwardedRef) forwardedRef.current = node;
      },
      [forwardedRef],
    );

    return (
      <RadixDialog.Portal>
        <RadixDialog.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-overlay backdrop-blur-[2px]',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
          )}
        />
        <RadixDialog.Content
          ref={setRefs}
          className={cn(
            'fixed z-50 flex min-h-0 flex-col gap-4 bg-surface text-fg shadow-e4',
            // Below the small breakpoint a dialog is a bottom sheet, because a
            // centred box with a virtual keyboard up leaves nothing readable.
            'inset-x-0 bottom-0 max-h-[92dvh] rounded-t-xl p-5',
            'sm:inset-auto sm:bottom-auto sm:top-1/2 sm:left-1/2 sm:w-full',
            'sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-xl sm:p-6',
            // Never overflow-hidden or overflow-auto. See the note above.
            'overflow-visible',
            SIZES[size],
            className,
          )}
          {...props}
        >
          <DialogPortalContext.Provider value={element}>
            {children}

            {hideCloseButton ? null : (
              <RadixDialog.Close
                aria-label={closeLabel}
                className={cn(
                  'absolute top-4 right-4 z-10 rounded-md p-1.5 text-fg-tertiary',
                  'transition-colors hover:bg-surface-sunken hover:text-fg',
                )}
              >
                <X className="h-4 w-4" />
              </RadixDialog.Close>
            )}
          </DialogPortalContext.Provider>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    );
  },
);

DialogContent.displayName = 'DialogContent';

export const DialogHeader = ({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) => <div className={cn('flex shrink-0 flex-col gap-1 pr-10', className)}>{children}</div>;

export const DialogTitle = forwardRef<
  ElementRef<typeof RadixDialog.Title>,
  ComponentPropsWithoutRef<typeof RadixDialog.Title>
>(({ className, ...props }, ref) => (
  <RadixDialog.Title
    ref={ref}
    className={cn('text-section-title font-semibold text-fg', className)}
    {...props}
  />
));

DialogTitle.displayName = 'DialogTitle';

export const DialogDescription = forwardRef<
  ElementRef<typeof RadixDialog.Description>,
  ComponentPropsWithoutRef<typeof RadixDialog.Description>
>(({ className, ...props }, ref) => (
  <RadixDialog.Description
    ref={ref}
    className={cn('text-body-sm text-fg-secondary', className)}
    {...props}
  />
));

DialogDescription.displayName = 'DialogDescription';

/**
 * Scrollable dialog body. Prefer this (or `FormDialog`) for any long content — never
 * put `overflow-auto` on `DialogContent`.
 *
 * Height is capped relative to the viewport so the header and footer stay put. The
 * scroll root uses `max-height` (not only `h-full`) so it constrains even when the
 * dialog itself is only max-height bounded.
 *
 * **`className`** styles the scroll shell (`ScrollArea`) — max-height, overflow-visible
 * for portaled pickers. **`contentClassName`** styles the inner content wrapper where
 * field spacing (`gap-6`, `space-y-4`) belongs. Spacing classes on `className` do not
 * affect layout between children.
 */
export const DialogBody = ({
  className,
  contentClassName,
  children,
  viewportClassName,
}: {
  className?: string;
  contentClassName?: string;
  children: ReactNode;
  viewportClassName?: string;
}) => (
  <ScrollArea
    className={cn('min-h-0 w-full max-h-[min(65dvh,36rem)]', className)}
    viewportClassName={viewportClassName}
  >
    {/*
      Symmetric horizontal inset keeps focus rings and outlined controls from being
      clipped by the ScrollArea's overflow-hidden root on either edge. A root-level
      `pr-*` gutter (meant for the overlay scrollbar) moved the right clip edge inward
      so wide controls — TimePicker in a two-column row — looked etched on the right
      after the left inset was added.
    */}
    <div className={cn('px-1.5 py-1', contentClassName)}>{children}</div>
  </ScrollArea>
);

/**
 * Actions are right-aligned with the primary or destructive action last, everywhere
 * in Trinity. The alignment is baked in here rather than left to each caller so it
 * cannot drift dialog by dialog.
 */
export const DialogFooter = ({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) => (
  <div
    className={cn(
      'flex w-full min-w-0 shrink-0 flex-col-reverse gap-2 pt-2 sm:flex-row sm:flex-wrap sm:justify-end',
      className,
    )}
  >
    {children}
  </div>
);
